import { useCallback, useEffect, useRef, useState } from 'react';

import type { IMicrophoneAudioTrack, ICameraVideoTrack } from 'agora-rtc-react';

import { isPermissionDeniedError } from '@/app/utils/errors';
import { useToast } from '@/components/Toast/context';

type DeviceTrack = IMicrophoneAudioTrack | ICameraVideoTrack;

const DEVICE_NAMES = {
  video: 'camera',
  audio: 'microphone',
} as const;

export default function useDeviceToggle(deviceTrack: DeviceTrack, deviceType: 'video' | 'audio') {
  const toast = useToast();
  const [isEnabled, setEnabled] = useState(Boolean(deviceTrack));
  const isToggling = useRef(false);

  // Turning the camera back on runs a fresh getUserMedia inside the SDK, and
  // that can fail: the device may be held by another app, or iOS may have torn
  // the capture down. The React flag must only follow a toggle that actually
  // happened — flipping it optimistically desyncs it from the SDK, whose
  // setEnabled short-circuits on the state it believes it is in, turning every
  // further tap into a no-op.
  const toggleDevice = useCallback(async () => {
    if (!deviceTrack || isToggling.current) {
      return;
    }
    isToggling.current = true;

    const newEnabled = !isEnabled;
    try {
      // The microphone mutes softly: the device stays captured, so unmuting
      // is instant and never goes back through getUserMedia — on iOS a new
      // capture request can interrupt the other track. The camera keeps
      // releasing the hardware, so its privacy light goes off.
      if (deviceType === 'audio') {
        await deviceTrack.setMuted(!newEnabled);
      } else {
        await deviceTrack.setEnabled(newEnabled);
      }
      setEnabled(newEnabled);
    } catch (error) {
      const deviceName = DEVICE_NAMES[deviceType];
      const text = isPermissionDeniedError(error as Error)
        ? `Please, enable ${deviceName}`
        : `Could not turn the ${deviceName} ${newEnabled ? 'on' : 'off'}. Please, try again`;
      toast.publish({ text, variant: 'destructive' });
    } finally {
      isToggling.current = false;
    }
  }, [deviceTrack, deviceType, isEnabled, toast]);

  useEffect(() => {
    if (deviceTrack) {
      setEnabled(Boolean(deviceTrack));
    }
  }, [deviceTrack]);

  // A track that dies outside our toggles — device unplugged, iOS reclaiming
  // the hardware after another capture started — still counts as enabled to
  // the SDK, so a plain re-enable would short-circuit. Cycling it off and on
  // forces a fresh acquisition; failing that, tell the truth: it is off.
  //
  // A muted microphone needs choreography: the SDK rejects setEnabled on a
  // muted track (TRACK_STATE_UNREACHABLE), and setMuted alone cannot restart
  // an ended capture. Lift the mute, cycle, then put the mute back.
  useEffect(() => {
    if (!deviceTrack) {
      return;
    }

    const reviveTrack = async () => {
      const restoreMuted = deviceType === 'audio' && !isEnabled;
      if (restoreMuted) {
        await deviceTrack.setMuted(false);
      }
      await deviceTrack.setEnabled(false);
      await deviceTrack.setEnabled(true);
      if (restoreMuted) {
        await deviceTrack.setMuted(true);
      }
    };

    const recoverEndedTrack = async () => {
      // A camera the user has turned off has nothing to revive, and a toggle
      // in flight owns the track's state — recovery must fight neither.
      if (isToggling.current || (deviceType === 'video' && !isEnabled)) {
        return;
      }
      try {
        await reviveTrack();
      } catch {
        setEnabled(false);
        toast.publish({
          text: `The ${DEVICE_NAMES[deviceType]} was disconnected. Check it and try again`,
          variant: 'destructive',
        });
      }
    };

    deviceTrack.on('track-ended', recoverEndedTrack);
    return () => {
      deviceTrack.off('track-ended', recoverEndedTrack);
    };
  }, [deviceTrack, deviceType, isEnabled, toast]);

  return { isEnabled, toggleDevice };
}
